import { useRef, useState } from 'react';
import { QueueTable, useQueueGrid } from '@components/QueueTable';
import { EventDrawer } from '@components/EventDrawer';
import { openEvent } from '@lib/open-mode';
import { FilterRail, RailSlicer } from '@components/FilterRail';
import { useEventQueue } from '@lib/useEventQueue';
import { inCampaign, useCampaign } from '@lib/campaigns';
import { CampaignScopeNote } from '@components/CampaignScopeNote';
import { slicerCount } from '@lib/grid';
import { STATUS_META, statusLabel, type Channel, type EventRow, type EventStatus } from '@lib/care-model';

/**
 * CallingQueue - the OUT-CALLING list, the feeder to the calls Care makes (his Care console
 * instructions, img9 and img10, 24 Sep 2026: "Make slicers on the side. This section is supposed to be a
 * feeder to the calling program - we call members - so make it for this").
 *
 * Separate page, not a chip on the inbound queue: a call list to work through, not member communications
 * to answer. The split is enforced SERVER-side (this page asks for source=out-calling; InboundQueue asks
 * for notSource=out-calling), so neither page pays for the other's rows. These events carry
 * direction='inbound' on purpose (the frozen resolution envelope and ConsentChase read direction);
 * `source` is the honest discriminator (see VALID_SOURCE in api/src/care/events.ts).
 *
 * THE FILTERS ARE ON THE RIGHT, in the sliding rail (slice A's FilterRail, the main portal's shape):
 *   Status     the former tabs To call / In progress / In review, one or more at once; opens on To call
 *   Worklist   the three worklist tabs of his workbook (they ride the row as its channel)
 *   Last call  not called yet, did not reach, reached (the boxes ticked in the call panel), or a call
 *              logged before the boxes existed
 *   and the table's own slicers (campaign, member, handled by), drawn into the rail.
 * His img9 note ("remove") took the paragraph that used to sit under the title.
 *
 * THE CALL itself is worked in the entry: the call panel (components/CallFeeder.tsx) shows the
 * recorded-line reminder before the call, opens Teams, and logs the boxes and the notes to the master.
 */

const STATUS_BASE: readonly EventStatus[] = ['new', 'in_progress', 'in_review'];
const STATUS_OPENING: ReadonlySet<EventStatus> = new Set<EventStatus>(['new']);
/** The calling list's own word for a new entry, as its tab said. */
export function callingStatusLabel(s: EventStatus): string {
  return s === 'new' ? 'To call' : statusLabel(s);
}

export const WORKLISTS: ReadonlyArray<{ key: Channel; label: string }> = [
  { key: 'Opt-in Not Returned', label: 'Opt-in not returned' },
  { key: 'Last-Yr Lapsed', label: 'Last-yr lapsed' },
  { key: 'Screen + Not Returned', label: 'Screen + not returned' },
];

export type LastCallKey = 'never' | 'not_reached' | 'reached' | 'no_boxes';
export const LAST_CALL_OPTIONS: ReadonlyArray<{ key: LastCallKey; label: string }> = [
  { key: 'never', label: 'Not called yet' },
  { key: 'not_reached', label: 'Did not reach' },
  { key: 'reached', label: 'Reached' },
  { key: 'no_boxes', label: 'Called (no boxes)' },
];

/** Which Last call value a row carries. Pure. */
export function lastCallKey(r: Pick<EventRow, 'lastCall'>): LastCallKey {
  const c = r.lastCall;
  if (!c) return 'never';
  if (c.reached === null) return 'no_boxes';
  return c.reached ? 'reached' : 'not_reached';
}

export const CALLING_RAIL_KEY = 'membercare.calling.rail-collapsed';

const passes = <K,>(sel: ReadonlySet<K>, v: K): boolean => sel.size === 0 || sel.has(v);
function sameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  return a.size === b.size && [...a].every((v) => b.has(v));
}

export function CallingQueue() {
  const [statusSel, setStatusSel] = useState<Set<EventStatus>>(() => new Set(STATUS_OPENING));
  const [worklistSel, setWorklistSel] = useState<Set<Channel>>(() => new Set());
  const [callSel, setCallSel] = useState<Set<LastCallKey>>(() => new Set());
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [banner, setBanner] = useState('');
  const [slicerHost, setSlicerHost] = useState<HTMLDivElement | null>(null);

  const { data, loading, reload } = useEventQueue({
    direction: 'inbound',
    // Every open status: the Status filter narrows in the browser (one or more at once).
    status: undefined,
    q,
    source: 'out-calling',
  });

  // ROW 354: the top-bar campaign scopes the list; a row naming no campaign always stays.
  const topCampaign = useCampaign();
  const loaded = data?.items ?? [];
  const all = loaded.filter((r) => inCampaign(r.campaign, topCampaign));
  const setAside = loaded.length - all.length;
  const byStatus = all.filter((r) => passes(statusSel, r.status));
  const items = byStatus.filter((r) => passes(worklistSel, r.channel) && passes(callSel, lastCallKey(r)));

  // Row 356: through the shared grid, newest first until she sorts, remembered per operator. Status is
  // the rail's own filter now, so the grid's slicers are the rest.
  const grid = useQueueGrid({
    table: 'calling',
    items,
    defaultSort: [{ k: 'date', asc: false }],
    slicers: ['campaign', 'match', 'handledBy'],
    showHandledBy: !sameSet(statusSel, STATUS_OPENING),
    callable: true,
  });

  // The counts: Status from the server's totals; Worklist and Last call over the rows the Status filter
  // keeps (what those two filters actually choose among).
  const totals = data?.totals as Partial<Record<EventStatus, number>> | undefined;
  const statusKeys: EventStatus[] = [...STATUS_BASE];
  for (const k of Object.keys(STATUS_META) as EventStatus[]) {
    if (!statusKeys.includes(k) && (totals?.[k] ?? 0) > 0) statusKeys.push(k);
  }
  const statusOptions = statusKeys.map((k) => ({ key: k, label: callingStatusLabel(k), count: totals?.[k] }));
  const worklistOptions = WORKLISTS.map((w) => ({ ...w, count: byStatus.filter((r) => r.channel === w.key).length }));
  const callOptions = LAST_CALL_OPTIONS.map((o) => ({ ...o, count: byStatus.filter((r) => lastCallKey(r) === o.key).length }));

  const gridSlicers = slicerCount(grid.view.slicers);
  const railActive = (statusSel.size > 0 ? 1 : 0) + (worklistSel.size > 0 ? 1 : 0) + (callSel.size > 0 ? 1 : 0) + gridSlicers;
  const railAtOpening = sameSet(statusSel, STATUS_OPENING) && worklistSel.size === 0 && callSel.size === 0 && gridSlicers === 0;
  const clearGridSlicers = grid.view.clearSlicers;
  const resetRail = () => {
    setStatusSel(new Set(STATUS_OPENING));
    setWorklistSel(new Set());
    setCallSel(new Set());
    clearGridSlicers();
  };

  // View-follows-the-work: a drawer press that moves the open row to a status the filter is not
  // showing ADDS that status, so the row does not silently vanish (the tabs used to switch).
  const statusRef = useRef(statusSel);
  statusRef.current = statusSel;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onDrawerChanged = (updated?: { eventId: string; status: string }) => {
    reload();
    if (!updated || updated.eventId !== selectedRef.current) return;
    const moved = updated.status as EventStatus;
    if (passes(statusRef.current, moved) || !STATUS_META[moved]) return;
    setStatusSel(new Set([...statusRef.current, moved]));
    setBanner(`Moved to ${callingStatusLabel(moved)}: added to the Status filter so it stays in view.`);
  };

  // page-wide with the queue-layout wrapper: the table keeps its own scroll box and never passes under the rail (slice A).
  return (
    <div className="page-pad page-wide">
      <header>
        <div className="section-eyebrow">Member Care · Out-calling</div>
        <h1 className="section-title">
          Calling <em>worklist</em>
        </h1>
        <div className="section-rule" />
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <input
          className="text-input"
          type="search"
          aria-label="Search the calling worklist"
          style={{ marginLeft: 'auto', maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search phone / name / member ID / text"
        />
      </div>

      <div className="queue-layout">
        <div className="queue-main">
          {banner && (
            <div className="note" style={{ marginBottom: 14 }}>
              {banner}
            </div>
          )}
          <QueueTable
            grid={grid}
            selectedId={selected}
            loading={loading}
            onSelect={(id) => openEvent(id, setSelected)}
            emptyLabel="No calls in this view."
            noun="calls"
            slicerHost={slicerHost}
          />
        </div>

        <FilterRail
          storageKey={CALLING_RAIL_KEY}
          activeCount={railActive}
          onReset={resetRail}
          canReset={!railAtOpening}
          scope={
            <>
              <span data-testid="rail-shown">
                {items.length.toLocaleString()} of {all.length.toLocaleString()} calls
              </span>
              <CampaignScopeNote others={setAside} />
            </>
          }
        >
          <RailSlicer label="Status" options={statusOptions} selected={statusSel} onChange={setStatusSel} />
          <RailSlicer label="Worklist" options={worklistOptions} selected={worklistSel} onChange={setWorklistSel} />
          <RailSlicer label="Last call" options={callOptions} selected={callSel} onChange={setCallSel} />
          <div ref={setSlicerHost} data-testid="rail-grid-slicers" />
        </FilterRail>
      </div>

      {selected && (
        <EventDrawer
          eventId={selected}
          hasAudioHint={items.find((r) => r.eventId === selected)?.hasAudio ?? false}
          onClose={() => setSelected(null)}
          onChanged={onDrawerChanged}
        />
      )}
    </div>
  );
}
